import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Loader2, Crown, Infinity as InfinityIcon, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Founding-Provider admin controls — one block per provider card on AdminPage.
 *
 * Backend:
 *   PATCH /api/admin/providers/{id}/founding  { isFoundingProvider?, lifetimeAccess?, referralCode? }
 *   GET   /api/admin/referrals               (used to look up invited / active counts)
 *
 * Keep it compact — just the toggles, the referral code, and counts.
 */
export default function FoundingProviderControls({ provider, onChanged }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [founder, setFounder] = useState(!!provider.isFoundingProvider);
  const [lifetime, setLifetime] = useState(!!provider.lifetimeAccess);
  const [code, setCode] = useState(provider.referralCode || '');
  const [editingCode, setEditingCode] = useState(false);
  const [draftCode, setDraftCode] = useState(code);
  const [savingFounder, setSavingFounder] = useState(false);
  const [savingLifetime, setSavingLifetime] = useState(false);
  const [savingCode, setSavingCode] = useState(false);
  const [stats, setStats] = useState({
    invitedCount: provider.referralCount || 0,
    activeReferralCount: null,
  });

  // Sync local state when the parent reloads with a fresh provider object
  useEffect(() => {
    setFounder(!!provider.isFoundingProvider);
    setLifetime(!!provider.lifetimeAccess);
    setCode(provider.referralCode || '');
    setStats({
      invitedCount: provider.referralCount || 0,
      activeReferralCount: null,
    });
  }, [provider.id, provider.isFoundingProvider, provider.lifetimeAccess, provider.referralCode, provider.referralCount]);

  const refreshStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/referrals`, { headers });
      const me = (res.data?.foundingProviders || []).find((p) => p.id === provider.id);
      if (me) {
        const invited = me.invited || [];
        const active = invited.filter(
          (i) => i.accountStatus === 'ACTIVE' && i.promoStatus === 'ACTIVE'
        ).length;
        setStats({
          invitedCount: me.referralCount || invited.length,
          activeReferralCount: active,
        });
      }
    } catch {
      // silent — stats are decorative
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider.id]);

  useEffect(() => {
    if (provider.isFoundingProvider) refreshStats();
  }, [provider.isFoundingProvider, refreshStats]);

  const patch = async (payload) => {
    const res = await axios.patch(
      `${API_URL}/api/admin/providers/${provider.id}/founding`,
      payload,
      { headers }
    );
    return res.data?.provider;
  };

  const onFounderChange = async (next) => {
    setFounder(next);
    setSavingFounder(true);
    try {
      const updated = await patch({ isFoundingProvider: next });
      if (updated?.referralCode && !code) setCode(updated.referralCode);
      toast.success(next ? 'Marked as Founding Provider' : 'Founding status revoked');
      onChanged?.(updated);
    } catch (e) {
      setFounder(!next);
      toast.error(e.response?.data?.detail || 'Could not update');
    } finally {
      setSavingFounder(false);
    }
  };

  const onLifetimeChange = async (next) => {
    setLifetime(next);
    setSavingLifetime(true);
    try {
      const updated = await patch({ lifetimeAccess: next });
      toast.success(next ? 'Lifetime access granted' : 'Lifetime access revoked');
      onChanged?.(updated);
    } catch (e) {
      setLifetime(!next);
      toast.error(e.response?.data?.detail || 'Could not update');
    } finally {
      setSavingLifetime(false);
    }
  };

  const generateCode = async () => {
    // Server auto-mints when we toggle isFoundingProvider on without a code,
    // OR when admin sends referralCode="" with founder=true. Simplest: just
    // flip founder ON if not already (server will mint), or PATCH with the
    // current owner-derived suggestion.
    setSavingCode(true);
    try {
      const updated = await patch({ isFoundingProvider: true });
      if (updated?.referralCode) {
        setCode(updated.referralCode);
        setDraftCode(updated.referralCode);
        toast.success('Referral code generated');
        onChanged?.(updated);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate');
    } finally {
      setSavingCode(false);
    }
  };

  const saveDraftCode = async () => {
    const cleaned = draftCode.trim().toUpperCase();
    if (!cleaned) {
      toast.error('Enter a code');
      return;
    }
    setSavingCode(true);
    try {
      const updated = await patch({ referralCode: cleaned });
      setCode(updated?.referralCode || cleaned);
      setEditingCode(false);
      toast.success('Referral code updated');
      onChanged?.(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code already in use');
    } finally {
      setSavingCode(false);
    }
  };

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Code copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div
      className="border-l-2 border-amber-300 pl-3 space-y-3 mt-4"
      data-testid={`founding-controls-${provider.id}`}
    >
      <div className="flex items-center gap-2">
        <Crown className="h-4 w-4 text-amber-600" />
        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
          Founding Provider
        </p>
        {founder && (
          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">
            Founder
          </Badge>
        )}
        {lifetime && (
          <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-[10px]">
            <InfinityIcon className="h-2.5 w-2.5 mr-0.5" /> Lifetime
          </Badge>
        )}
      </div>

      {/* Toggles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="flex items-center justify-between p-2 rounded border bg-card text-sm">
          <div>
            <p className="font-medium">Founding Provider</p>
            <p className="text-xs text-muted-foreground">Eligible to refer with extended perks</p>
          </div>
          <div className="flex items-center gap-2">
            {savingFounder && <Loader2 className="h-3 w-3 animate-spin" />}
            <Switch
              checked={founder}
              onCheckedChange={onFounderChange}
              disabled={savingFounder}
              data-testid={`founder-toggle-${provider.id}`}
            />
          </div>
        </div>
        <div className="flex items-center justify-between p-2 rounded border bg-card text-sm">
          <div>
            <p className="font-medium">Lifetime Access</p>
            <p className="text-xs text-muted-foreground">Bypass subscription forever</p>
          </div>
          <div className="flex items-center gap-2">
            {savingLifetime && <Loader2 className="h-3 w-3 animate-spin" />}
            <Switch
              checked={lifetime}
              onCheckedChange={onLifetimeChange}
              disabled={savingLifetime}
              data-testid={`lifetime-toggle-${provider.id}`}
            />
          </div>
        </div>
      </div>

      {/* Referral code */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">Referral code:</span>
        {editingCode ? (
          <>
            <Input
              value={draftCode}
              onChange={(e) => setDraftCode(e.target.value)}
              className="h-8 text-sm max-w-xs"
              placeholder="RW-NAME-2026"
              data-testid={`referral-code-input-${provider.id}`}
            />
            <Button size="sm" onClick={saveDraftCode} disabled={savingCode} data-testid={`referral-code-save-${provider.id}`}>
              {savingCode ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditingCode(false); setDraftCode(code); }}>
              Cancel
            </Button>
          </>
        ) : code ? (
          <>
            <code
              className="px-2 py-0.5 rounded bg-muted text-sm font-mono"
              data-testid={`referral-code-${provider.id}`}
            >
              {code}
            </code>
            <Button size="sm" variant="ghost" onClick={copyCode} data-testid={`referral-code-copy-${provider.id}`}>
              <Copy className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setDraftCode(code); setEditingCode(true); }}>
              Edit
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={generateCode}
            disabled={savingCode}
            data-testid={`generate-referral-code-${provider.id}`}
          >
            {savingCode ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-3 w-3 mr-1" />
            )}
            Generate Referral Code
          </Button>
        )}
      </div>

      {/* Stats */}
      {(founder || code) && (
        <div className="grid grid-cols-2 gap-2 text-sm" data-testid={`referral-stats-${provider.id}`}>
          <div className="rounded border bg-card p-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Invited</p>
            <p className="text-lg font-bold">{stats.invitedCount}</p>
          </div>
          <div className="rounded border bg-card p-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Active referrals</p>
            <p className="text-lg font-bold">
              {stats.activeReferralCount == null ? '—' : stats.activeReferralCount}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
